// @ts-strict-ignore
import * as React from 'react';
import { Event } from '../../utils/analytics';
import { RootState } from '../../reducers/reducers';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import {
    selectAllAppIdsInAppGroup,
    selectAllAppNamesAndPlatformsByAppId,
    selectAllAppsIdsInOrg,
    selectApp,
    selectAppHome,
    selectOrg,
    selectUser,
    selectOrgOverviewAverageApprovalItemDurations,
    selectStatisticsById,
    selectIsWorkflowsCIStepEnabled,
    selectAllAppGroupNamesInOrg,
} from '../../selectors/selectors';
import {
    selectDestinationsData,
    selectCanAccessTabularView,
    selectHasAnyWorkflowsApp,
} from '../../selectors/summaryViewSelectors';
import { getDefaultVersionsForDestinations, getValidVersionForDestinations } from '../../helpers/summaryViewHelpers';
import { getAppGroupsForOrgId, orgOverviewStatsRequestId } from '../../helpers';
import { AppSet, OrgComponentProps, OrgSettingsTabType } from '../../typescriptHelpers';
import { antGrey, blurryBackdropStyle, pageContentStyle, runwayColors, titleStyle } from '../styles/globalStyles';
import Layout, { Content } from 'antd/lib/layout/layout';
import { SettingFilled, GlobalOutlined } from '@ant-design/icons';
import Space from 'antd/lib/space';
import Avatar from 'antd/lib/avatar';
import Text from 'antd/lib/typography/Text';
import Spin from 'antd/lib/spin';
import {
    AppEntity,
    AppGroup,
    OrgOverviewApprovalItemDurations,
    OrgOverviewStatistic,
    StatisticCategory,
    StatisticScope,
    StatisticTimeSeriesDataPoint,
    StatisticType,
} from '../../types';
import { PageViewType } from '@/types/summaryView';
import Button from 'antd/lib/button';
import Divider from 'antd/lib/divider';
import Typography from 'antd/lib/typography';
import { OrgOverviewEntityRequest, OrgOverviewFilters, tryFetchEntity } from '../../actions/entities';
import { FetchableEntityType } from '../../entityType';
import { useIsLoading } from '@/utils/hooks/helpers';
import { LiveReleaseSummaryCard, NextReleaseSummaryCard } from '../ReleaseSummaryCard';
import { SummaryTable } from '../orgSummary/SummaryTable';
import { useSummaryView } from '@/utils/hooks/useSummaryView';
import { OverviewTableHeader, PageViewContext } from '../orgSummary/OverviewTableHeader';
import { createFilterApplyTracker } from '@/helpers/summaryViewHelpers';
import { createOrgSettingsRoute } from '../../helpers/routeHelpers';
import { Link } from 'react-router-dom';
import { trackOrgRouteEvent } from '../../actions/trackEvent';
import { compact, flatMap, flatten, pick } from 'lodash';
import { AppPlatformOrAll, OrgOverviewAppSetsTabBar, StatisticCategoryOrAll } from './OrgOverviewAppGroupsTabBar';
import { displayTitleForStatCategory } from './orgOverviewHelpers';
import {
    OrgOverviewDurationsBarGraphStat,
    OrgOverviewReleaseStepDurations,
    OrgOverviewStatCard,
    OrgOverviewStatEmptyCard,
    OrgOverviewStatLoadingCard,
} from './OrgOverviewStatCards';
import { EmptyStateWrapper, featureSectionForStatisticCategory } from '../releaseEmptyState/EmptyStateWrapper';
import { AverageApprovalsBarGraph } from '../releaseOverview/AverageApprovalsBarGraph';

const MIN_STAT_CARD_WIDTH = 288;

const ORG_WIDE_STATISTICS: StatisticType[] = [
    StatisticType.ReleaseDuration,
    StatisticType.ItemsCompleted,
    StatisticType.FixRequestsCount,
    StatisticType.FixRequestsApproved,
    StatisticType.FixRequestsRejected,
    StatisticType.PercentWorkItemsInFixRequests,
    StatisticType.WaitingForReviewDuration,
    StatisticType.InReviewDuration,
    StatisticType.AppStoreBuildBundleDownloadSize,
    StatisticType.AppStoreBuildBundleInstallSize,
    StatisticType.CrashFreeSessions,
    StatisticType.CrashFreeUsers,
    StatisticType.BetaCrashFreeSessions,
    StatisticType.BetaCrashFreeUsers,
    StatisticType.StatAverageCustomerRating,
    StatisticType.HotfixReleaseDuration,
    StatisticType.StatAverageTimeToRecover,
    StatisticType.StatFailureRate,
    StatisticType.StatAverageCLIApprovalTime,
    StatisticType.StatAverageReleaseStepCompletionTime,
    StatisticType.StatReleaseFrequency,
    StatisticType.StatReleaseDurations,
    StatisticType.CIAverageBuildTime,
    StatisticType.TotalHotfixesCount,
    StatisticType.LinesOfCodeChanged,
    StatisticType.StatAverageAppCustomerRating,
];

const APP_WIDE_STATISTICS: StatisticType[] = [
    StatisticType.ReleaseDuration,
    StatisticType.ItemsCompleted,
    StatisticType.FixRequestsCount,
    StatisticType.FixRequestsApproved,
    StatisticType.FixRequestsRejected,
    StatisticType.PercentWorkItemsInFixRequests,
    StatisticType.WaitingForReviewDuration,
    StatisticType.InReviewDuration,
    StatisticType.CrashFreeSessions,
    StatisticType.CrashFreeUsers,
    StatisticType.BetaCrashFreeSessions,
    StatisticType.BetaCrashFreeUsers,
    StatisticType.AppStoreBuildCount,
    StatisticType.CIWorkflowSuccessPercent,
    StatisticType.CommitCount,
    StatisticType.AppStoreBuildBundleDownloadSize,
    StatisticType.AppStoreBuildBundleInstallSize,
    StatisticType.LinesOfCodeChanged,
    StatisticType.FilesChangedCount,
    StatisticType.StatAverageCustomerRating,
    StatisticType.HotfixReleaseDuration,
    StatisticType.StatAverageTimeToRecover,
    StatisticType.StatFailureRate,
    StatisticType.StatAverageCLIApprovalTime,
    StatisticType.StatAverageReleaseStepCompletionTime,
    StatisticType.StatReleaseFrequency,
    StatisticType.StatReleaseDurations,
    StatisticType.CIAverageBuildTime,
    StatisticType.TotalHotfixesCount,
    StatisticType.StatAverageAppCustomerRating,
];

interface StatCategorySectionProps {
    title?: string;
    isLoading: boolean;
    stats: OrgOverviewStatistic[];
    category: StatisticCategory;
    scope: StatisticScope;
    selectedAppPlatform: AppPlatformOrAll;
    averageApprovalItemDurations?: OrgOverviewApprovalItemDurations;
    isWorkflowsCIStepEnabled: boolean;
}

const StatCategorySection: React.FC<StatCategorySectionProps> = ({
    isLoading,
    stats,
    title,
    scope,
    selectedAppPlatform,
    category,
    averageApprovalItemDurations,
    isWorkflowsCIStepEnabled,
}) => {
    const appNamesAndPlatformsByAppId = useSelector(selectAllAppNamesAndPlatformsByAppId);
    const isEmpty = !isLoading && stats.length === 0;

    return (
        <Space direction="vertical" style={{ width: '100%', marginTop: 30 }} size={20}>
            <div
                style={{
                    display: 'flex',
                    gap: 20,
                    alignItems: 'center',
                }}
            >
                {title && (
                    <>
                        <Typography.Text
                            style={{
                                fontSize: 20,
                                color: antGrey.grey7,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {title}
                        </Typography.Text>
                        <div
                            style={{
                                flex: 1,
                                height: '100%',
                            }}
                        >
                            <Divider
                                type="horizontal"
                                style={{
                                    width: '100%',
                                    margin: 0,
                                }}
                            />
                        </div>
                    </>
                )}
            </div>

            <EmptyStateWrapper
                featureSections={[featureSectionForStatisticCategory(category)]}
                shouldCenterContent
                shouldForceEnable={isEmpty}
                appId={null}
                releaseId={null}
                extraInfo={{
                    scope,
                }}
            >
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_STAT_CARD_WIDTH}px, 1fr))`,
                        gap: 20,
                        flex: 1,
                        gridAutoFlow: 'row',
                    }}
                >
                    {isLoading ?
                        Array(4)
                            .fill(0)
                            .map((_, i) => <OrgOverviewStatLoadingCard key={i} />)
                    : isEmpty ?
                        Array(4)
                            .fill(0)
                            .map((_, i) => <OrgOverviewStatEmptyCard key={i} />)
                    :   stats.map((stat, i) => {
                            if (stat.type === StatisticType.StatAverageReleaseStepCompletionTime) {
                                return (
                                    <OrgOverviewReleaseStepDurations
                                        key={i}
                                        {...stat}
                                        scope={scope}
                                        isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                                    />
                                );
                            }

                            if (stat.type === StatisticType.StatReleaseDurations) {
                                return <OrgOverviewDurationsBarGraphStat key={i} {...stat} scope={scope} />;
                            }

                            if (stat.type === StatisticType.StatAverageAppCustomerRating) {
                                const shouldDisplayDestination =
                                    scope !== StatisticScope.Org && selectedAppPlatform !== 'all';
                                const { destinationId, destinationName, platform } =
                                    shouldDisplayDestination && stat.dataPoints?.length ?
                                        (stat.dataPoints[0] as StatisticTimeSeriesDataPoint)
                                    :   ({
                                            destinationId: null,
                                            destinationName: null,
                                            platform: null,
                                        } as StatisticTimeSeriesDataPoint);

                                return (
                                    <OrgOverviewStatCard
                                        key={i}
                                        {...stat}
                                        scope={scope}
                                        appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                                        shouldDisplayDestination={shouldDisplayDestination}
                                        destinationId={destinationId}
                                        destinationName={destinationName}
                                        platform={platform}
                                    />
                                );
                            }

                            return (
                                <OrgOverviewStatCard
                                    key={i}
                                    {...stat}
                                    scope={scope}
                                    appNamesAndPlatformsByAppId={appNamesAndPlatformsByAppId}
                                />
                            );
                        })
                    }
                </div>

                {averageApprovalItemDurations && category == StatisticCategory.DevOps && (
                    <AverageApprovalsBarGraph
                        approvalDurations={averageApprovalItemDurations.averageApprovalItemDurations}
                        numReleases={averageApprovalItemDurations.numReleases}
                    />
                )}
            </EmptyStateWrapper>
        </Space>
    );
};

const OrgOverview: React.FC<OrgComponentProps> = (props: OrgComponentProps) => {
    const dispatch = useDispatch();

    const [trackedPageView, setTrackedPageView] = React.useState<string | null>(null);
    const handlePageViewTracking = React.useCallback((pageView: string) => {
        setTrackedPageView((prev) => (prev === pageView ? prev : pageView));
    }, []);

    const [selectedAppSetIndex, setSelectedAppSetIndex] = React.useState(0);
    const [selectedAppPlatform, setSelectedAppPlatform] = React.useState<AppPlatformOrAll>('all');
    const [selectedStatCategory, setSelectedStatCategory] = React.useState<StatisticCategoryOrAll>('all');
    const [selectedStatFilters, setSelectedStatFilters] = React.useState<OrgOverviewFilters>(null);

    const orgDestinationsData = useSelector((state: RootState) => selectDestinationsData(state, props.orgId));
    const isLoadingOrgDestinations = useIsLoading(FetchableEntityType.OrgDestinationsAndVersions, props.orgId);

    React.useEffect(() => {
        if (!orgDestinationsData && !isLoadingOrgDestinations) {
            dispatch(
                tryFetchEntity(FetchableEntityType.OrgDestinationsAndVersions, { orgId: props.orgId }, props.orgId),
            );
        }
    }, [dispatch, props.orgId, orgDestinationsData, isLoadingOrgDestinations]);

    const { orgId, orgName, orgImageUrl } = useSelector((state: RootState) => {
        const org = selectOrg(state, props.orgId);
        return {
            orgId: org.orgId,
            orgName: org.name,
            orgImageUrl: org.orgImageUrl,
        };
    }, shallowEqual);

    const canAccessTabularView = useSelector((state: RootState) => selectCanAccessTabularView(state, props.orgId));
    const hasAnyWorkflowsApp = useSelector((state: RootState) => selectHasAnyWorkflowsApp(state, props.orgId));
    const showTabularView = hasAnyWorkflowsApp || canAccessTabularView;

    React.useEffect(() => {
        if (!showTabularView) {
            handlePageViewTracking(PageViewType.Default);
        }
    }, [showTabularView, handlePageViewTracking]);

    const statsRequestId = selectedStatFilters ? orgOverviewStatsRequestId(selectedStatFilters) : null;

    const statistics = useSelector((state: RootState) => {
        if (statsRequestId) {
            return selectStatisticsById(state, statsRequestId);
        }

        return null;
    });

    const singleAppId = selectedStatFilters?.appId;
    const liveReleaseSummary = useSelector((state: RootState) => {
        if (singleAppId) {
            const appHome = selectAppHome(state, singleAppId);
            return appHome?.liveRelease;
        }
        return null;
    });
    const nextReleaseSummary = useSelector((state: RootState) => {
        if (singleAppId) {
            const appHome = selectAppHome(state, singleAppId);
            return appHome?.nextRelease;
        }
        return null;
    });
    const avgApprovalItemDurations = useSelector((state: RootState) => {
        if (singleAppId) {
            return selectOrgOverviewAverageApprovalItemDurations(state, statsRequestId);
        }
        return null;
    });

    // the app ids to fetch fresh devops stats for
    const statAppIds = useSelector((state: RootState) => {
        if (selectedStatFilters) {
            if (selectedStatFilters.orgId) {
                return selectAllAppsIdsInOrg(state, selectedStatFilters.orgId);
            } else if (selectedStatFilters.appGroupId) {
                return selectAllAppIdsInAppGroup(state, selectedStatFilters.appGroupId);
            } else if (selectedStatFilters.appId) {
                return [selectedStatFilters.appId];
            }
        }

        return [];
    });

    const appGroupsWithApps: { appGroup: AppGroup; apps: AppEntity[] }[] = useSelector((state: RootState) => {
        const appGroups = getAppGroupsForOrgId(props.orgId, state);

        return appGroups.map((appGroup) => {
            return {
                appGroup,
                apps: compact(appGroup.appIds.map((appId) => selectApp(state, appId))),
            };
        });
    });

    const isOrgOverviewLoading = useIsLoading(FetchableEntityType.OrgOverviewV2, statsRequestId);

    React.useEffect(() => {
        if (!trackedPageView) return;
        dispatch(
            trackOrgRouteEvent({
                orgId: props.orgId,
                eventName: Event.ViewOrgOverview,
                viewType: trackedPageView,
            }),
        );
    }, [dispatch, props.orgId, trackedPageView]);

    const appSets: AppSet[] = appGroupsWithApps.map((appGroupWithApps) => {
        const apps = appGroupWithApps.apps;

        const appGroupId = appGroupWithApps.appGroup.id;

        const statsRequestId = orgOverviewStatsRequestId({
            appGroupId,
        });

        return {
            id: appGroupId,
            statsRequestId,
            name: appGroupWithApps.appGroup.name,
            apps: apps.map((app) => {
                return {
                    id: app.appId,
                    imageUrl: app.appSettings.appImageUrl,
                    platform: app.appSettings.platform,
                };
            }),
        };
    });
    const allAppsSetApps = flatMap(appSets, (appSet) => appSet.apps);

    React.useEffect(() => {
        // reset selected platform
        setSelectedAppPlatform('all');

        // reset stat filters
        if (selectedAppSetIndex === 0) {
            setSelectedStatFilters({ orgId: props.orgId });
        } else {
            const appGroupId = appSets[selectedAppSetIndex - 1]?.id;
            setSelectedStatFilters({ appGroupId });
        }
    }, [selectedAppSetIndex]);

    React.useEffect(() => {
        // TODO: Handle analytic events
        // const properties = allReleasePropertiesFrom(org);
        // const isAllSelected = index === 0; // all apps is always the first tab
        // const currentAppSet = isAllSelected ? null : appSets[index];
        // AnalyticEvents.track(AnalyticEvents.Event.ClickOrgOverviewAppGroupApp, {
        //     ...properties,
        //     appGroupName: currentAppSet.name,
        //     ...(isAllSelected
        //         ? {}
        //         : {
        //               appName: currentAppSet.appId,
        //           }),
        //     platform: isAllSelected ? 'all' : currentApp.appSettings.platform,
        // });
    }, [selectedAppSetIndex, selectedAppPlatform, selectedStatCategory]);

    const allAppSets = [
        {
            id: props.orgId,
            name: 'All apps',
            apps: allAppsSetApps,
            icon: GlobalOutlined,
            statsRequestId: orgOverviewStatsRequestId({ orgId: props.orgId }),
        },
        ...appSets,
    ];

    let scope: StatisticScope = StatisticScope.Org;
    if (selectedAppSetIndex !== 0) {
        scope = StatisticScope.AppGroup;
    }
    if (selectedAppPlatform !== 'all') {
        scope = StatisticScope.App;
    }

    const statisticsByType =
        (statistics?.reduce((acc, stat) => {
            acc[stat.type] = [...(acc[stat.type] ?? []), stat];
            return acc;
        }, {}) as Record<string, OrgOverviewStatistic[]>) ?? {};

    const allowListedStats = scope === StatisticScope.Org ? ORG_WIDE_STATISTICS : APP_WIDE_STATISTICS;
    const statisticsByCategory = flatten(Object.values(pick(statisticsByType, allowListedStats)))
        .sort((a, b) => allowListedStats.indexOf(a.type) - allowListedStats.indexOf(b.type))
        .filter((statistic) => statistic.category === selectedStatCategory || selectedStatCategory === 'all')
        .reduce(
            (acc, statistic) => ({
                ...acc,
                [statistic.category]: [...(acc[statistic.category] ?? []), statistic],
            }),
            {} as Record<StatisticCategory, OrgOverviewStatistic[]>,
        );

    const allStatCategories = Object.values(StatisticCategory);

    const shouldShowAllCategories = selectedStatCategory === 'all';

    const categoriesToRender =
        shouldShowAllCategories ? allStatCategories : [selectedStatCategory as StatisticCategory];

    const onPlatformSelected = (platform: AppPlatformOrAll, appId?: string) => {
        if (platform === 'all' && appSets.length > 0) {
            const appGroupId = appSets[selectedAppSetIndex - 1]?.id;
            setSelectedStatFilters({ appGroupId });
        } else {
            setSelectedStatFilters({ appId });
        }

        setSelectedAppPlatform(platform);
    };

    const [calendarDatesHaveChanged, setCalendarDatesHaveChanged] = React.useState(false);

    React.useEffect(() => {
        if ((!statistics && statsRequestId && statAppIds.length > 0) || calendarDatesHaveChanged) {
            const request: OrgOverviewEntityRequest<OrgOverviewFilters> = {
                orgId: props.orgId,
                filters: selectedStatFilters,
                appIds: statAppIds,
            };
            dispatch(tryFetchEntity(FetchableEntityType.OrgOverviewV2, request, statsRequestId));
            setCalendarDatesHaveChanged(false);
        }
    }, [statistics, statsRequestId, selectedStatFilters, calendarDatesHaveChanged]);

    const appStoreRatingStat = statisticsByType[StatisticType.StatAverageCustomerRating];

    const onFinish = (values) => {
        const [start, end] = values['metricsDateRange'];
        const parsedStart = start.format('YYYY-MM-DD');
        const parsedEnd = end.format('YYYY-MM-DD');
        setSelectedStatFilters((prevData) => ({ ...prevData, startDate: parsedStart, endDate: parsedEnd }));
        setCalendarDatesHaveChanged(true);
    };

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, singleAppId),
    );

    const renderChartsView = () => (
        <>
            <div
                style={{
                    position: 'sticky',
                    top: 0,
                    left: 0,
                    padding: '0 32px',
                    marginLeft: -32,
                    paddingTop: 32,
                    zIndex: 2,
                    width: 'calc(100% + 64px)',
                    ...blurryBackdropStyle,
                }}
            >
                <OrgOverviewAppSetsTabBar
                    appSets={allAppSets}
                    isLoading={isOrgOverviewLoading}
                    selectedAppSetIndex={selectedAppSetIndex}
                    onAppSetSelected={(index) => {
                        setSelectedAppSetIndex(index);
                    }}
                    selectedPlatform={selectedAppPlatform}
                    onPlatformSelected={onPlatformSelected}
                    selectedStatCategory={selectedStatCategory}
                    onStatCategorySelected={(statCategory) => {
                        setSelectedStatCategory(statCategory);
                    }}
                    calendarDatesHaveChanged={calendarDatesHaveChanged}
                    onFinish={onFinish}
                />
            </div>
            <div
                style={{
                    position: 'relative',
                    marginBottom: 56,
                }}
            >
                {shouldShowAllCategories && singleAppId && (
                    <div style={{ display: 'flex', flex: 1, flexWrap: 'wrap', gap: 20, marginTop: 32 }}>
                        {liveReleaseSummary && (
                            <LiveReleaseSummaryCard
                                liveReleaseSummary={liveReleaseSummary}
                                appId={singleAppId}
                                appStoreRatingStat={appStoreRatingStat?.[0]}
                            />
                        )}
                        {nextReleaseSummary && (
                            <NextReleaseSummaryCard nextReleaseSummary={nextReleaseSummary} appId={singleAppId} />
                        )}
                    </div>
                )}

                {categoriesToRender.map((category) => {
                    const stats: OrgOverviewStatistic[] = statisticsByCategory[category] ?? [];
                    return (
                        <StatCategorySection
                            key={category}
                            category={category}
                            title={shouldShowAllCategories ? displayTitleForStatCategory(category) : null}
                            isLoading={isOrgOverviewLoading}
                            stats={stats}
                            scope={scope}
                            selectedAppPlatform={selectedAppPlatform}
                            averageApprovalItemDurations={avgApprovalItemDurations}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />
                    );
                })}
            </div>
        </>
    );

    return (
        <Layout
            style={{
                ...pageContentStyle,
                paddingTop: 0,
                paddingBottom: 0,
            }}
        >
            <Content
                style={{
                    overflowY: 'scroll',
                    overflowX: 'hidden',
                    paddingLeft: 32,
                    paddingRight: 32,
                    position: 'relative',
                }}
            >
                <div
                    style={{
                        zIndex: 1,
                        width: '100%',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            paddingTop: 24,
                        }}
                    >
                        <Avatar src={orgImageUrl} style={{ marginRight: 20 }} size={80} />
                        <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Text style={{ fontSize: 24, marginBottom: 6, fontWeight: 600, color: antGrey.grey8 }}>
                                    {orgName}
                                </Text>
                                <Link to={createOrgSettingsRoute(orgId, OrgSettingsTabType.Team)}>
                                    <Button icon={<SettingFilled style={{ color: runwayColors.primary }} />}>
                                        Settings
                                    </Button>
                                </Link>
                            </div>
                            <Text style={titleStyle}>Overview</Text>
                        </div>
                    </div>
                </div>
                {isLoadingOrgDestinations ?
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            width: '100%',
                        }}
                    >
                        <Spin size="large" />
                        <Text style={{ color: runwayColors.primary, paddingTop: 10 }}>Loading...</Text>
                    </div>
                : showTabularView ?
                    <div style={{ marginTop: 32 }}>
                        <OrgOverviewWorkflowsContent
                            orgId={orgId}
                            renderChartsView={renderChartsView}
                            onPageViewChange={handlePageViewTracking}
                        />
                    </div>
                :   renderChartsView()}
            </Content>
        </Layout>
    );
};

export default OrgOverview;

interface OrgOverviewWorkflowsContentProps {
    orgId: string;
    renderChartsView: () => React.ReactNode;
    onPageViewChange: (pageView: string) => void;
}

const OrgOverviewWorkflowsContent: React.FC<OrgOverviewWorkflowsContentProps> = ({
    orgId,
    renderChartsView,
    onPageViewChange,
}) => {
    const summary = useSummaryView({ orgId });
    const {
        preferences,
        loading: { isLoadingInitialData: isLoadingOrgDestinations },
        availableVersions,
        availableDestinationIds,
        initializeWithDefaults,
        pagination: { destinationLimit },
    } = summary;

    const showCharts = preferences.pageView === PageViewType.Default;

    const org = useSelector((state: RootState) => selectOrg(state, orgId));
    const user = useSelector((state: RootState) => selectUser(state));
    const appsInOrg = useSelector((state: RootState) => selectAllAppsIdsInOrg(state, orgId));
    const appGroupsInOrg = useSelector((state: RootState) => selectAllAppGroupNamesInOrg(state, orgId));
    const destinationsData = useSelector((state: RootState) => selectDestinationsData(state, orgId));

    const handleTrackFilterApply = React.useMemo(
        () => createFilterApplyTracker(org, user, appsInOrg, appGroupsInOrg, preferences, PageViewContext.OrgOverview),
        [org, user, appsInOrg, appGroupsInOrg, preferences],
    );

    React.useEffect(() => {
        const { pageView, tabularLayout } = preferences;
        const viewType = pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
        onPageViewChange(viewType);
    }, [onPageViewChange, preferences.pageView, preferences.tabularLayout]);

    const initialDestinationIds = React.useMemo(() => {
        if (availableDestinationIds.length === 0) return [];
        return destinationLimit ? availableDestinationIds.slice(0, destinationLimit) : availableDestinationIds;
    }, [availableDestinationIds, destinationLimit]);

    const defaultVersions = React.useMemo(() => {
        const validVersion = getValidVersionForDestinations(initialDestinationIds, destinationsData);
        if (validVersion) return [validVersion];
        return getDefaultVersionsForDestinations(availableVersions);
    }, [initialDestinationIds, destinationsData, availableVersions]);

    React.useEffect(() => {
        if (isLoadingOrgDestinations) return;
        if (!destinationLimit) return;
        if (initialDestinationIds.length === 0 || availableVersions.length === 0) return;

        initializeWithDefaults({
            versions: defaultVersions,
            destinationIds: initialDestinationIds,
        });
    }, [
        isLoadingOrgDestinations,
        destinationLimit,
        initialDestinationIds,
        availableVersions.length,
        defaultVersions,
        initializeWithDefaults,
    ]);

    return (
        <>
            <OverviewTableHeader
                context={PageViewContext.OrgOverview}
                summary={summary}
                onTrackFilterApply={handleTrackFilterApply}
            />
            {showCharts ? renderChartsView() : <SummaryTable summary={summary} />}
        </>
    );
};
